export type WalletLoginIntent = {
	walletType: string;
	autoOpenModal: boolean;
	autoStartLogin: boolean;
	createdAt: number;
	source: "external-browser" | "wallet-browser";
};

const STORAGE_KEY = "wallet_login_intent_v1";
const PARAM_WALLET = "wallet";
const PARAM_AUTO_OPEN = "walletLogin";
const PARAM_AUTO_START = "walletAutoStart";
const PARAM_SOURCE = "walletSource";

export function buildWalletLoginIntentUrl(walletType: string): string {
	if (typeof window === "undefined") {
		return "";
	}

	const url = new URL(window.location.href);
	url.searchParams.set(PARAM_WALLET, walletType);
	url.searchParams.set(PARAM_AUTO_OPEN, "1");
	url.searchParams.set(PARAM_AUTO_START, "1");
	url.searchParams.set(PARAM_SOURCE, "external-browser");
	return url.toString();
}

export function createWalletLoginIntent(walletType: string): WalletLoginIntent {
	return {
		walletType,
		autoOpenModal: true,
		autoStartLogin: true,
		createdAt: Date.now(),
		source: "external-browser",
	};
}

export function persistWalletLoginIntent(intent: WalletLoginIntent): void {
	if (typeof window === "undefined") {
		return;
	}

	window.localStorage.setItem(STORAGE_KEY, JSON.stringify(intent));
}

export function getStoredWalletLoginIntent(): WalletLoginIntent | null {
	if (typeof window === "undefined") {
		return null;
	}

	const raw = window.localStorage.getItem(STORAGE_KEY);
	if (!raw) {
		return null;
	}

	try {
		const parsed = JSON.parse(raw) as WalletLoginIntent;
		if (!parsed?.walletType) {
			return null;
		}
		return parsed;
	} catch {
		return null;
	}
}

export function clearStoredWalletLoginIntent(): void {
	if (typeof window === "undefined") {
		return;
	}

	window.localStorage.removeItem(STORAGE_KEY);
}

export function getWalletLoginIntentFromUrl(): WalletLoginIntent | null {
	if (typeof window === "undefined") {
		return null;
	}

	const url = new URL(window.location.href);
	const walletType = url.searchParams.get(PARAM_WALLET);

	if (!walletType || url.searchParams.get(PARAM_AUTO_OPEN) !== "1") {
		return null;
	}

	return {
		walletType,
		autoOpenModal: true,
		autoStartLogin: url.searchParams.get(PARAM_AUTO_START) === "1",
		createdAt: Date.now(),
		source:
			url.searchParams.get(PARAM_SOURCE) === "wallet-browser"
				? "wallet-browser"
				: "external-browser",
	};
}

export function consumeWalletLoginIntentFromUrl(): WalletLoginIntent | null {
	if (typeof window === "undefined") {
		return null;
	}

	const intent = getWalletLoginIntentFromUrl();
	if (!intent) {
		return null;
	}

	const url = new URL(window.location.href);
	url.searchParams.delete(PARAM_WALLET);
	url.searchParams.delete(PARAM_AUTO_OPEN);
	url.searchParams.delete(PARAM_AUTO_START);
	url.searchParams.delete(PARAM_SOURCE);
	window.history.replaceState({}, "", url.toString());

	return intent;
}
